import React, { useEffect, useMemo, useState } from 'react';
import { authenticatedFetch } from '../../utils/auth';
import './GenericPdfExtractor.css';

const INPUT_TOKEN_COST_PER_MILLION = 3.0;
const OUTPUT_TOKEN_COST_PER_MILLION = 15.0;

const GenericPdfExtractor = ({ userInfo, onBack }) => {
  const auditorId = userInfo?.user_id || '';
  const [engagements, setEngagements] = useState([]);
  const [selectedEngagementId, setSelectedEngagementId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [documentsScopeEngagementId, setDocumentsScopeEngagementId] = useState('');
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [sectionExtractions, setSectionExtractions] = useState([]);
  const [sectionRuns, setSectionRuns] = useState([]);
  const [newEngagementName, setNewEngagementName] = useState('');
  const [isCreatingEngagement, setIsCreatingEngagement] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileInputResetToken, setFileInputResetToken] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadJobId, setUploadJobId] = useState('');
  const [uploadJobStatus, setUploadJobStatus] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [preferSectionMode, setPreferSectionMode] = useState(true);
  const [expandedTables, setExpandedTables] = useState({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const selectedEngagement = useMemo(
    () => engagements.find((item) => item.id === selectedEngagementId) || null,
    [engagements, selectedEngagementId]
  );
  const hasSelectedDocumentInList = useMemo(
    () => documents.some((item) => item.document_id === selectedDocumentId),
    [documents, selectedDocumentId]
  );
  const extractionCostSummary = useMemo(() => {
    const completedRuns = sectionRuns.filter((run) => (run.status || '').toLowerCase() === 'completed');
    const inputTokens = completedRuns.reduce((sum, run) => sum + Number(run.prompt_tokens || 0), 0);
    const outputTokens = completedRuns.reduce((sum, run) => sum + Number(run.completion_tokens || 0), 0);
    const costUsd = (
      (inputTokens / 1_000_000) * INPUT_TOKEN_COST_PER_MILLION +
      (outputTokens / 1_000_000) * OUTPUT_TOKEN_COST_PER_MILLION
    );
    return {
      inputTokens,
      outputTokens,
      costUsd,
    };
  }, [sectionRuns]);

  useEffect(() => {
    loadExtractorEngagements();
  }, []);

  useEffect(() => {
    if (!selectedEngagementId || !auditorId) return;
    loadEngagementDocuments(selectedEngagementId);
  }, [selectedEngagementId, auditorId]);

  useEffect(() => {
    if (
      !selectedDocumentId ||
      !selectedEngagementId ||
      !auditorId ||
      !hasSelectedDocumentInList ||
      documentsScopeEngagementId !== selectedEngagementId
    ) return;
    loadDocumentExtraction(selectedDocumentId, selectedEngagementId);
  }, [selectedDocumentId, selectedEngagementId, auditorId, hasSelectedDocumentInList, documentsScopeEngagementId]);

  useEffect(() => {
    if (!uploadJobId || !selectedEngagementId || !auditorId) return;
    const timer = window.setInterval(async () => {
      try {
        const response = await authenticatedFetch(
          `/api/v1/document-intake/job/${uploadJobId}?auditor_id=${encodeURIComponent(auditorId)}&engagement_id=${encodeURIComponent(selectedEngagementId)}`
        );
        if (!response.ok) return;
        const data = await response.json();
        const status = (data.status || '').toLowerCase();
        setUploadJobStatus(status);
        if (status === 'completed') {
          window.clearInterval(timer);
          setUploadJobId('');
          setPendingFiles([]);
          setFileInputResetToken((prev) => prev + 1);
          setNotice('Upload and parsing completed.');
          await loadEngagementDocuments(selectedEngagementId);
        } else if (status === 'failed') {
          window.clearInterval(timer);
          setUploadJobId('');
          setError(data.error_message || 'Upload processing failed.');
        }
      } catch (err) {
        setError(`Failed to poll upload job: ${err.message}`);
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [uploadJobId, selectedEngagementId, auditorId]);

  useEffect(() => {
    if (!isExtracting || !selectedDocumentId || !selectedEngagementId || !auditorId) return;
    const timer = window.setInterval(async () => {
      try {
        const response = await authenticatedFetch(
          `/api/v1/document-intake/documents/${selectedDocumentId}/section-extractions/status?auditor_id=${encodeURIComponent(auditorId)}&engagement_id=${encodeURIComponent(selectedEngagementId)}`
        );
        if (!response.ok) return;
        const data = await response.json();
        const runs = data.runs || [];
        setSectionRuns(runs);
        const anyRunning = runs.some((run) => (run.status || '').toLowerCase() === 'running');
        if (!anyRunning && runs.length > 0) {
          window.clearInterval(timer);
          setIsExtracting(false);
          await loadDocumentExtraction(selectedDocumentId, selectedEngagementId);
        }
      } catch (err) {
        setError(`Failed to poll extraction status: ${err.message}`);
      }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [isExtracting, selectedDocumentId, selectedEngagementId, auditorId]);

  const loadExtractorEngagements = async () => {
    setError('');
    try {
      const response = await authenticatedFetch('/api/v1/document-intake/extractor/engagements');
      const data = await response.json();
      const items = data.engagements || [];
      setEngagements(items);
      if (!selectedEngagementId && items.length > 0) {
        setDocumentsScopeEngagementId('');
        setDocuments([]);
        setSelectedDocumentId('');
        setSectionExtractions([]);
        setSectionRuns([]);
        setSelectedEngagementId(items[0].id);
      }
    } catch (err) {
      setError(`Failed to load extractor engagements: ${err.message}`);
    }
  };

  const loadEngagementDocuments = async (engagementId) => {
    setError('');
    setDocumentsScopeEngagementId('');
    setPendingFiles([]);
    setFileInputResetToken((prev) => prev + 1);
    setSelectedDocumentId('');
    setSectionExtractions([]);
    setSectionRuns([]);
    setDocuments([]);
    try {
      const response = await authenticatedFetch(
        `/api/v1/document-intake/documents?auditor_id=${encodeURIComponent(auditorId)}&engagement_id=${encodeURIComponent(engagementId)}`
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Failed to load documents');
      }
      const data = await response.json();
      const docs = data.documents || [];
      setDocuments(docs);
      setDocumentsScopeEngagementId(engagementId);
      if (!docs.some((item) => item.document_id === selectedDocumentId)) {
        setSelectedDocumentId(docs[0]?.document_id || '');
      }
      if (docs.length === 0) {
        setSectionExtractions([]);
        setSectionRuns([]);
      }
    } catch (err) {
      setDocumentsScopeEngagementId('');
      setError(`Failed to load documents: ${err.message}`);
    }
  };

  const loadDocumentExtraction = async (documentId, engagementId) => {
    setError('');
    try {
      const [sectionsRes, runsRes] = await Promise.all([
        authenticatedFetch(
          `/api/v1/document-intake/documents/${documentId}/section-extractions?auditor_id=${encodeURIComponent(auditorId)}&engagement_id=${encodeURIComponent(engagementId)}`
        ),
        authenticatedFetch(
          `/api/v1/document-intake/documents/${documentId}/section-extractions/status?auditor_id=${encodeURIComponent(auditorId)}&engagement_id=${encodeURIComponent(engagementId)}`
        ),
      ]);
      if (sectionsRes.ok) {
        const sectionsJson = await sectionsRes.json();
        setSectionExtractions(sectionsJson.sections || []);
      }
      if (runsRes.ok) {
        const runsJson = await runsRes.json();
        setSectionRuns(runsJson.runs || []);
      }
    } catch (err) {
      setError(`Failed to load extraction data: ${err.message}`);
    }
  };

  const handleCreateEngagement = async () => {
    const name = (newEngagementName || '').trim();
    if (!name) {
      setError('Engagement name is required.');
      return;
    }
    setIsCreatingEngagement(true);
    setError('');
    setNotice('');
    try {
      const response = await authenticatedFetch('/api/v1/document-intake/extractor/engagements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create engagement');
      }
      setNewEngagementName('');
      setNotice('Extractor engagement created.');
      await loadExtractorEngagements();
      const newId = data.engagement?.id;
      if (newId) {
        setDocumentsScopeEngagementId('');
        setDocuments([]);
        setSelectedDocumentId('');
        setSectionExtractions([]);
        setSectionRuns([]);
        setSelectedEngagementId(newId);
      }
    } catch (err) {
      setError(`Create engagement failed: ${err.message}`);
    } finally {
      setIsCreatingEngagement(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedEngagementId) {
      setError('Select an engagement first.');
      return;
    }
    if (!auditorId) {
      setError('Missing user context.');
      return;
    }
    if (!pendingFiles.length) {
      setError('Select at least one PDF file.');
      return;
    }
    setIsUploading(true);
    setError('');
    setNotice('');
    try {
      const formData = new FormData();
      formData.append('auditor_id', auditorId);
      formData.append('engagement_id', selectedEngagementId);
      pendingFiles.forEach((file) => formData.append('files', file));
      const response = await authenticatedFetch('/api/v1/document-intake/upload', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Upload failed');
      }
      setUploadJobId(data.job_id || '');
      setUploadJobStatus('processing');
      setNotice('Upload accepted. Processing started.');
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRunExtraction = async () => {
    if (!selectedEngagementId || !selectedDocumentId) {
      setError('Select an engagement and a document first.');
      return;
    }
    const extractionMode = preferSectionMode ? 'auto_section' : 'page';
    const selectedProfile = 'generic_pdf';
    setError('');
    setNotice('');
    try {
      const formData = new FormData();
      formData.append('auditor_id', auditorId);
      formData.append('engagement_id', selectedEngagementId);
      formData.append('extraction_profile', selectedProfile);
      formData.append('extraction_mode', extractionMode);
      const response = await authenticatedFetch(
        `/api/v1/document-intake/documents/${selectedDocumentId}/section-extractions/ai`,
        { method: 'POST', body: formData }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to trigger extraction');
      }
      setIsExtracting(true);
      setNotice(`Extraction started: ${selectedProfile} / ${extractionMode}`);
      await loadDocumentExtraction(selectedDocumentId, selectedEngagementId);
    } catch (err) {
      setError(`Extraction trigger failed: ${err.message}`);
    }
  };

  const renderTableBlock = (sectionId, table, tableIndex) => {
    const tableKey = `${sectionId}-tbl-${tableIndex}`;
    const columns = Array.isArray(table.columns) ? table.columns : [];
    const rows = Array.isArray(table.rows) ? table.rows : [];
    const isExpanded = Boolean(expandedTables[tableKey]);
    const visibleRows = isExpanded ? rows : rows.slice(0, 5);
    return (
      <div key={tableKey} className="table-preview-card">
        <div className="table-preview-title">
          {table.title || `Table ${tableIndex + 1}`}
          <span className="muted">{rows.length} row(s)</span>
        </div>
        {columns.length > 0 ? (
          <div className="table-preview-scroll">
            <table className="table-preview-grid">
              <thead>
                <tr>
                  {columns.map((column, columnIndex) => (
                    <th key={`${tableKey}-col-${columnIndex}`}>
                      {column || `Column ${columnIndex + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, rowIndex) => (
                  <tr key={`${tableKey}-row-${rowIndex}`}>
                    {columns.map((_, columnIndex) => (
                      <td key={`${tableKey}-row-${rowIndex}-col-${columnIndex}`}>
                        {Array.isArray(row) ? (row[columnIndex] ?? '') : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="muted">Table columns not available.</div>
        )}
        {rows.length > 5 && (
          <div className="table-row-toggle">
            <span className="muted">
              {isExpanded ? `Showing all ${rows.length} rows.` : `Showing first 5 of ${rows.length} rows.`}
            </span>
            <button
              type="button"
              className="table-toggle-btn"
              onClick={() => setExpandedTables((prev) => ({ ...prev, [tableKey]: !isExpanded }))}
            >
              {isExpanded ? 'Show less' : 'Show all'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderDisplayBlock = (sectionId, block, blockIndex) => {
    const blockType = block?.block_type || 'unknown';
    if (blockType === 'table') {
      return renderTableBlock(
        `${sectionId}-block`,
        {
          title: block.title,
          columns: block.columns,
          rows: block.rows,
        },
        blockIndex
      );
    }

    if (blockType === 'text') {
      const text = (block.text || '').trim();
      if (!text) return null;
      const anchorParts = [];
      if (block.page_number) anchorParts.push(`p.${block.page_number}`);
      if (block.block_index) anchorParts.push(`block ${block.block_index}`);
      const anchorLabel = anchorParts.join(' · ');
      return (
        <div key={`${sectionId}-blk-${blockIndex}`} className="extractor-text-block">
          {anchorLabel && <div className="extractor-block-anchor muted">{anchorLabel}</div>}
          <pre className="extractor-text-pre">{text}</pre>
        </div>
      );
    }

    if (blockType === 'sub_section') {
      const title = (block.title || '').trim();
      const tagLabel = block.tag ? `tag=${block.tag}` : '';
      const confidenceLabel = block.confidence ? `confidence=${block.confidence}` : '';
      const meta = [tagLabel, confidenceLabel].filter(Boolean).join(' · ');
      if (!title && !meta) return null;
      return (
        <div key={`${sectionId}-blk-${blockIndex}`} className="extractor-subsection-block">
          {title && <h4 className="extractor-subsection-heading">{title}</h4>}
          {meta && <div className="muted">{meta}</div>}
        </div>
      );
    }

    if (blockType === 'figure') {
      return (
        <div key={`${sectionId}-blk-${blockIndex}`} className="extractor-block">
          <div className="extractor-block-title">
            Figure
            <span className="muted">{block.type || 'other'}</span>
          </div>
          <div className="muted">caption={block.caption || '-'} · page_index={block.page_index || '-'}</div>
        </div>
      );
    }

    return (
      <details key={`${sectionId}-blk-${blockIndex}`} className="extractor-block">
        <summary className="extractor-block-title">
          Unknown field
          <span className="muted">{block.field_name || blockType}</span>
        </summary>
        <pre className="extractor-json-pre">{JSON.stringify(block.value ?? block, null, 2)}</pre>
      </details>
    );
  };

  return (
    <div className="generic-pdf-extractor">
      <aside className="extractor-sidebar">
        <div className="extractor-sidebar-header">
          <h2>PDF Extractor</h2>
          <button type="button" onClick={onBack}>Back</button>
        </div>

        <div className="extractor-create-row">
          <input
            type="text"
            value={newEngagementName}
            onChange={(e) => setNewEngagementName(e.target.value)}
            placeholder="New engagement name"
          />
          <button type="button" onClick={handleCreateEngagement} disabled={isCreatingEngagement}>
            {isCreatingEngagement ? 'Creating...' : '+ New'}
          </button>
        </div>

        <div className="extractor-engagement-list">
          {engagements.length === 0 && <div className="muted">No engagements yet.</div>}
          {engagements.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`engagement-item ${selectedEngagementId === item.id ? 'active' : ''}`}
              onClick={() => {
                setDocumentsScopeEngagementId('');
                setDocuments([]);
                setSelectedDocumentId('');
                setSectionExtractions([]);
                setSectionRuns([]);
                setPendingFiles([]);
                setFileInputResetToken((prev) => prev + 1);
                setSelectedEngagementId(item.id);
              }}
            >
              <div className="name">{item.name}</div>
              <div className="meta">{item.status}</div>
            </button>
          ))}
        </div>
      </aside>

      <main className="extractor-main">
        <header className="extractor-main-header">
          <div className="extractor-main-title-row">
            <h1>{selectedEngagement?.name || 'Select engagement'}</h1>
            <div className="extractor-cost">
              Cost of extraction: US$ {extractionCostSummary.costUsd.toFixed(2)}
            </div>
          </div>
          <div className="sub">
            Auditor: {auditorId || '-'} · Engagement: {selectedEngagementId || '-'}
          </div>
        </header>

        {error && <div className="banner error">{error}</div>}
        {notice && <div className="banner notice">{notice}</div>}

        <section className="panel">
          <h3>Upload PDF</h3>
          <div className="row">
            <input
              key={`${selectedEngagementId}-${fileInputResetToken}`}
              type="file"
              multiple
              accept="application/pdf,.pdf"
              onChange={(e) => setPendingFiles(Array.from(e.target.files || []))}
            />
            <button type="button" onClick={handleUpload} disabled={isUploading || !pendingFiles.length}>
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          {uploadJobId && (
            <div className="muted">Job: {uploadJobId} · Status: {uploadJobStatus || 'processing'}</div>
          )}
        </section>

        <section className="panel">
          <h3>Documents</h3>
          <div className="document-list">
            {documents.length === 0 && <div className="muted">No documents uploaded.</div>}
            {documents.map((doc) => (
              <button
                key={doc.document_id}
                type="button"
                className={`document-item ${selectedDocumentId === doc.document_id ? 'active' : ''}`}
                onClick={() => setSelectedDocumentId(doc.document_id)}
              >
                <span>{doc.filename}</span>
                <span className="muted">{doc.processing_status}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="panel">
          <h3>Extraction Controls</h3>
          <div className="row">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={preferSectionMode}
                onChange={(e) => setPreferSectionMode(Boolean(e.target.checked))}
              />
              I want to select pages (auto_section mode)
            </label>
            <button
              type="button"
              onClick={handleRunExtraction}
              disabled={!selectedDocumentId || isExtracting}
            >
              {isExtracting ? 'Extracting...' : 'Run Extraction'}
            </button>
          </div>
        </section>

        <section className="panel">
          <h3>Section I-III Extraction Runs</h3>
          {sectionRuns.length === 0 && <div className="muted">No runs yet.</div>}
          {sectionRuns.map((run) => (
            <div key={run.run_id} className="run-row">
              <span>{run.run_type}</span>
              <span className={`status ${(run.status || '').toLowerCase()}`}>{run.status}</span>
              <span className="muted">{run.page_start}-{run.page_end}</span>
            </div>
          ))}
        </section>

        <section className="panel">
          <h3>Extracted Data (DB)</h3>
          {sectionExtractions.length === 0 && <div className="muted">No extracted rows yet.</div>}
          {sectionExtractions.map((section) => {
            const flags = section.flags_json || {};
            const displayPayload = flags.display_payload || {};
            const displayBlocks = Array.isArray(displayPayload.blocks) ? displayPayload.blocks : [];
            const rawParsed = flags.generic_pdf_output || {};
            const fallbackFullText = typeof rawParsed.page_text_all === 'string' ? rawParsed.page_text_all.trim() : '';
            const previewSource = (
              displayPayload.text_primary ||
              section.raw_text ||
              rawParsed.page_text_all ||
              rawParsed.section_text ||
              ''
            );
            const preview = previewSource.slice(0, 280);
            const extractedTables = Array.isArray(flags.tables) ? flags.tables : [];
            const hasTableOnlyContent = !preview && extractedTables.length > 0;
            const hasDisplayBlocks = displayBlocks.length > 0;
            const hasDisplayTextBlock = hasDisplayBlocks && displayBlocks.some(
              (block) => block?.block_type === 'text' && (block?.text || '').trim()
            );
            return (
              <article key={section.id} className="section-card">
                <div className="section-head">
                  <strong>{section.section_heading || 'Untitled'}</strong>
                  <span className="muted">p.{section.page_start}-{section.page_end}</span>
                </div>
                <div className="muted">
                  profile={flags.extraction_profile || '-'} · mode={flags.extraction_mode || '-'}
                </div>
                <p>
                  {preview
                    ? `${preview}${previewSource.length > 280 ? '...' : ''}`
                    : hasTableOnlyContent
                      ? `No narrative text extracted. ${extractedTables.length} table(s) extracted.`
                      : 'No text extracted.'}
                </p>
                {hasDisplayBlocks ? (
                  <div className="table-preview-list">
                    {displayBlocks.map((block, blockIndex) => renderDisplayBlock(section.id, block, blockIndex))}
                  </div>
                ) : extractedTables.length > 0 && (
                  <div className="table-preview-list">
                    {extractedTables.map((table, tableIndex) => renderTableBlock(section.id, table, tableIndex))}
                  </div>
                )}
                {!hasDisplayTextBlock && fallbackFullText && (
                  <div className="extractor-block">
                    <div className="extractor-block-title">
                      Full extracted text
                      <span className="muted">fallback</span>
                    </div>
                    <pre className="extractor-text-pre">{fallbackFullText}</pre>
                  </div>
                )}
                {flags.generic_pdf_output && (
                  <details className="extractor-raw-details">
                    <summary className="extractor-block-title">Raw AI JSON</summary>
                    <pre className="extractor-json-pre">{JSON.stringify(flags.generic_pdf_output, null, 2)}</pre>
                  </details>
                )}
              </article>
            );
          })}
        </section>
      </main>
    </div>
  );
};

export default GenericPdfExtractor;
